import { useCallback, useState } from "react";
import globalAxios from "axios";

/** One collapsible group (a section of pages, or a content type of queued items). */
export interface DeploymentSummaryGroup {
	key: string;
	label: string;
	total: number;
	breakdown: Record<string, number>;
}

export interface DeploymentSummaryTotals {
	outOfSyncPages: number;
	outOfSyncSingletons: number;
	queued: number;
}

export interface DeploymentSummary {
	target: string;
	totals: DeploymentSummaryTotals;
	pageGroups: DeploymentSummaryGroup[];
	queueGroups: DeploymentSummaryGroup[];
}

const EMPTY: DeploymentSummary = {
	target: "preview",
	totals: { outOfSyncPages: 0, outOfSyncSingletons: 0, queued: 0 },
	pageGroups: [],
	queueGroups: []
};

/**
 * Loads the grouped "difference from target" snapshot (GET /api/deployment/summary)
 * — the single clear view that replaces the flat thousands-row list + dual counters.
 * Uses the app's shared API base path so it behaves like every other admin call.
 */
export const useDeploymentSummary = (target: "preview" | "production") => {
	const [summary, setSummary] = useState<DeploymentSummary>(EMPTY);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async (cacheBust?: number) => {
		setLoading(true);
		setError(null);
		try {
			// Relative URL → uses axios.defaults.baseURL (REACT_APP_API_URL in dev,
			// same-origin in prod), exactly like the rest of the admin's API calls.
			const res = await globalAxios.get<DeploymentSummary>(`/api/deployment/summary`, {
				withCredentials: true,
				headers: { Accept: "application/json" },
				params: { target, ...(cacheBust != null && { _: cacheBust }) }
			});
			const data = res.data ?? EMPTY;
			setSummary({
				target: data.target ?? target,
				totals: data.totals ?? EMPTY.totals,
				pageGroups: Array.isArray(data.pageGroups) ? data.pageGroups : [],
				queueGroups: Array.isArray(data.queueGroups) ? data.queueGroups : []
			});
		} catch (err) {
			console.error(err);
			setSummary(EMPTY);
			setError("Unable to load the deployment summary.");
		} finally {
			setLoading(false);
		}
	}, [target]);

	return { summary, loading, error, load };
};
